import type { ReactElement } from 'react';
import type { ExtensionRiskEstimate } from '@kirin/api-client/contracts/extensions';
import { AgentmeshTagBadge } from '@/components/shared/agentmesh/AgentmeshTagBadge';
import { LabelLessState } from './LabelLessState';
import {
  NO_VERDICT_DESCRIPTION,
  NO_VERDICT_LABEL,
  NOT_APPLICABLE_DESCRIPTION,
  NOT_APPLICABLE_LABEL,
  RISK_BUCKET_LABELS,
} from './agentmesh-risk-labels';

interface RiskEstimateCellProps {
  /** Optional on list rows until enrichment runs; null = no verdict. */
  riskEstimate: ExtensionRiskEstimate | undefined;
  /**
   * A built-in without a verdict (see isNotApplicable): reads "Not
   * applicable" instead of "No verdict yet", since no scan is coming.
   */
  notApplicable?: boolean;
  /**
   * Explain the label-less state on hover and to screen readers. Off where
   * the explanation is already on screen (the expanded row).
   */
  describe?: boolean;
}

/**
 * Risk cell for inventory extension rows (KIR-5208): AgentMesh's verdict in
 * its own buckets — Dangerous, Risky or Clean — and "No verdict yet" without
 * one ("Not applicable" for a built-in). On a collapsed row it is the worst
 * verdict across the installed versions. How much was scanned is the
 * separate Scan status cell.
 */
export function RiskEstimateCell({
  riskEstimate,
  notApplicable = false,
  describe = true,
}: RiskEstimateCellProps): ReactElement {
  const bucket = riskEstimate?.riskBucket ?? null;
  if (bucket === null) {
    return (
      <LabelLessState
        label={notApplicable ? NOT_APPLICABLE_LABEL : NO_VERDICT_LABEL}
        description={
          notApplicable ? NOT_APPLICABLE_DESCRIPTION : NO_VERDICT_DESCRIPTION
        }
        describe={describe}
      />
    );
  }
  return (
    <span
      className="inline-block max-w-full truncate"
      title={RISK_BUCKET_LABELS[bucket]}
    >
      <AgentmeshTagBadge tag={bucket} />
    </span>
  );
}

RiskEstimateCell.displayName = 'RiskEstimateCell';
